/* global CriteriaHelper */
import React from 'react';
import PropTypes from 'prop-types';

// Components
import { BooleanInput } from './ruleEditorViewParts/BooleanInput';
import { DataInputConcept } from './ruleEditorViewParts/DataInputConcept';
import { DateInput } from './ruleEditorViewParts/DateInput';
import { DataInputQuantity } from './ruleEditorViewParts/DataInputQuantity';
import { DateRangeInput } from './ruleEditorViewParts/DateRangeInput';
import { FunctionGlobalResource } from './ruleEditorViewParts/FunctionGlobalResource';
import { IdentifierInput } from './ruleEditorViewParts/IdentifierInput';
import { OperatorResource } from './ruleEditorViewParts/OperatorResource';
import { PlainRender } from './ruleEditorViewParts/PlainRender';
import { GenericPartLabelRenderer } from './ruleEditorViewParts/GenericPartLabelRenderer';
import { TextInput } from './ruleEditorViewParts/TextInput';
import { NumericInput } from './ruleEditorViewParts/NumericInput';

// Hooks
import { useCriteriaStorePredicate } from '../../../../../../state/criteria/hooks/useCriteriaStorePredicate';

const InputElementsByType = {
    dataInputConcept: {
        Component: DataInputConcept
    },
    operatorResource: {
        Component: OperatorResource
    },
    functionResource: {
        Component: FunctionGlobalResource
    },
    globalResource: {
        Component: FunctionGlobalResource
    },
    unsupportedResource: {
        Component: GenericPartLabelRenderer
    },
    dataInputNumeric: {
        Component: NumericInput
    },
    dataInputQuantity: {
        Component: DataInputQuantity
    },
    dataInputString: {
        Component: TextInput,
        props: {
            placeholder: 'Enter text value...',
            type: 'string'
        }
    },
    text: {
        Component: PlainRender
    },
    dataInputBoolean: {
        Component: BooleanInput
    },
    dataInputIdentifier: {
        Component: IdentifierInput
    },
    dataInputDateRange: {
        Component: DateRangeInput
    },
    dataInputDate: {
        Component: DateInput
    },
    dataInputNodeDTO: {
        Component: GenericPartLabelRenderer
    },
    dataInputKeyword: {
        Component: TextInput,
        props: {
            placeholder: 'Enter keyword...',
            type: 'keyword'
        }
    },
    dataInputEval: {
        Component: TextInput,
        props: {
            placeholder: 'Enter eval value...',
            type: 'eval'
        }
    },
    plainRender: {
        Component: PlainRender
    }
};

const placeholderGroupPart = {
    partId: null,
    partType: 'Text',
    text: 'Unlabeled Group',
    sourcePredicatePartDTO: { overridable: false },
    predicatePartConcepts: []
};

export const PredicatePartInner = ({ predicatePartUuid, predicateType }) => {
    const predicatePart = useCriteriaStorePredicate(predicatePartUuid);
    const partClassification = predicatePart ? CriteriaHelper.getPartClassificationKey(predicatePart) : null;
    const PartInput = InputElementsByType[partClassification];
    const { functionEnd, parameterEnd } = predicatePart ?? {};

    return (
        <div style={{ display: 'flex', zIndex: 1, alignItems: 'center', gap: '8px' }}>
            {PartInput ? (
                <PartInput.Component
                    id={predicatePartUuid}
                    predicateType={predicateType}
                    partClassification={partClassification}
                    {...(PartInput.props ?? {})}
                    {...(['plainRender', 'text'].includes(partClassification) ? { part: predicatePart } : {})}
                />
            ) : null}
            {functionEnd && <span>)</span>}
            {parameterEnd && <span>,</span>}
        </div>
    );
};

PredicatePartInner.propTypes = {
    predicatePartUuid: PropTypes.string,
    predicateType: PropTypes.string
};

export const PredicatePart = ({ predicatePartUuid, predicateType, isPlaceholder }) => {
    if (isPlaceholder) {
        return <PlainRender predicateType={predicateType} part={placeholderGroupPart}></PlainRender>;
    }

    return <PredicatePartInner {...{ predicatePartUuid, predicateType }} />;
};

export default PredicatePart;

PredicatePart.propTypes = {
    isPlaceholder: PropTypes.bool,
    predicatePartUuid: PropTypes.string,
    predicateType: PropTypes.string
};
